import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { CommunityCard } from './CommunityCard';
import FeaturedCommunityCard from './FeaturedCommunityCard';
import { CommunityFilters, CommunityFilterState } from './CommunityFilters';
import BaseCard from '../common/layout/BaseCard';
import Empty from '../common/display/Empty';
import { captureAppException } from '../../utils/errorCaptureUtils';
import { Community } from './types';
import { useAuth } from '../../lib/AuthContext';
import { useLocation } from 'react-router-dom';
import CommunityAnnouncementCard from './CommunityAnnouncementCard';
import Masonry from 'react-masonry-css';
import { captureCustomEvent } from '../../utils/posthogUtils';

export const CommunitiesPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [communities, setCommunities] = useState<Community[]>([]);
  const [loading, setLoading] = useState(true);
  const [filteredCommunities, setFilteredCommunities] = useState<Community[]>([]);
  const [filters, setFilters] = useState<CommunityFilterState>({
    search: ''
  });
  // Store workspace admin roles for the user
  const [adminWorkspaceIds, setAdminWorkspaceIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('my_communities') === 'true') {
      setFilters(f => ({ ...f, myCommunities: true }));
    } else {
      setFilters(f => ({ ...f, myCommunities: false }));
    }
  }, [location.search]);

  useEffect(() => {
    fetchCommunities();
  }, []);

  useEffect(() => {
    if (!loading) {
      filterCommunities();
    }
  }, [communities, filters, user]);

  // Fetch all workspace roles for the user on mount
  useEffect(() => {
    const fetchWorkspaceRoles = async () => {
      if (!user?.id) return;
      // Fetch all workspace_user_roles for the user
      const { data, error } = await supabase
        .from('workspace_users')
        .select(`workspace_id, workspace_user_roles:workspace_user_roles(*, workspace_roles(*))`)
        .eq('user_id', user.id);
      if (error) return;
      // Find all workspace_ids where the user has an admin role
      const adminRoles = ['owner', 'editor', 'community_manager'];
      const adminIds = new Set<string>();
      (data || []).forEach((wu: any) => {
        if (wu.workspace_user_roles && Array.isArray(wu.workspace_user_roles)) {
          wu.workspace_user_roles.forEach((wur: any) => {
            if (wur.workspace_roles && adminRoles.includes(wur.workspace_roles.name)) {
              adminIds.add(wu.workspace_id);
            }
          });
        }
      });
      setAdminWorkspaceIds(adminIds);
    };
    fetchWorkspaceRoles();
  }, [user?.id]);

  const fetchCommunities = async () => {
    try {
      const { data, error } = await supabase
        .from('communities')
        .select(`
          id,
          name,
          description,
          header_image,
          avatar_image,
          display_priority,
          display_highlight,
          discoverability,
          approval_status,
          workspace:workspaces!workspace_id (
            workspace_id,
            workspace_name,
            logo_url
          ),
          members:community_members!community_id (
            user_id,
            role,
            users (
              id,
              first_name,
              profile_image_url
            )
          )
        `)
        .order('display_priority', { ascending: false })
        .order('name', { ascending: true });

      if (error) throw error;

      const processedCommunities = (data || []).map((community: any) => {
        // Use user_id, first_name, and profile_image_url for each member.
        // Avatar uses first_name for fallback.
        const members = (community.members || []).map((m: any) => ({
          user_id: m.user_id,
          role: m.role,
          first_name: m.users?.first_name ?? '',
          last_name: '',
          profile_image_url: m.users?.profile_image_url ?? null,
        }));

        // Get public URL for header image if it exists
        const headerImageUrl = community.header_image
          ? supabase.storage.from('community_images').getPublicUrl(community.header_image).data.publicUrl
          : null;

        // Get public URL for avatar image if it exists
        const avatarImageUrl = community.avatar_image
          ? supabase.storage.from('community_images').getPublicUrl(community.avatar_image).data.publicUrl
          : null;

        return {
          ...community,
          members,
          total_members: members.length,
          header_image: headerImageUrl,
          avatar_image: avatarImageUrl,
          workspace: community.workspace
        } as Community;
      });

      setCommunities(processedCommunities);
      setFilteredCommunities(processedCommunities);
    } catch (error) {
      captureAppException(error, {
        tags: { context: 'communities_page', action: 'fetch_communities' },
      });
      setCommunities([]);
      setFilteredCommunities([]);
    } finally {
      setLoading(false);
    }
  };

  const filterCommunities = () => {
    setLoading(true);
    let filtered = [...communities];

    if (filters.myCommunities && user?.id) {
      // Show all communities the user is a member of (except banned, so includes invited/pending)
      // OR communities where the user can admin (owner/editor/community_manager in workspace)
      filtered = filtered.filter((c) => {
        const isMember = c.members.some((m) => m.user_id === user.id && m.role !== 'banned');
        const canAdmin = c.workspace && c.workspace.workspace_id && adminWorkspaceIds.has(c.workspace.workspace_id);
        return isMember || canAdmin;
      });
    } else {
      // Only show public and approved/official communities
      filtered = filtered.filter(
        (c) =>
          c.discoverability === 'public' &&
          (c.approval_status === 'approved' || c.approval_status === 'official')
      );
    }

    setFilteredCommunities(filtered);
    setLoading(false);
  };

  const handleCommunityView = (community: Community) => {
    captureCustomEvent('community_discovery', {
      eventProperties: {
        community_id: community.id,
        community_name: community.name,
        source: 'communities_listing',
        is_highlighted: community.display_highlight
      }
    });
  };

  const featuredCommunity = filteredCommunities.find(c => c.display_priority >= 1000);
  const regularCommunities = featuredCommunity
    ? filteredCommunities.filter(c => c.id !== featuredCommunity.id)
    : filteredCommunities;

  const breakpointColumns = {
    default: 3,    // 3 columns at large screens (1350px+)
    1350: 2,       // 2 columns at medium-large screens (1000px - 1350px)
    1000: 1        // 1 column at medium and below (<1000px)
  };

  return (
    <div className="space-y-6">
      <BaseCard overflow='visible'>
        <div className="flex flex-col sm:relative">
          <div className="text-gray-500">
            Communities
          </div>
          <h1 className="text-h5 font-bold text-gray-900 mt-1 mb-4 sm:mb-12">Discover Communities</h1>
        </div>

        <CommunityFilters
          onFiltersChange={setFilters}
          myCommunitiesActive={!!filters.myCommunities}
        />
      </BaseCard>

      {loading ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : regularCommunities.length > 0 ? (
        <>
          {/* Featured Community and Announcement Card */}
          {featuredCommunity && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="col-span-1 lg:col-span-2 order-1">
                <FeaturedCommunityCard
                  community={{
                    id: featuredCommunity.id,
                    name: featuredCommunity.name,
                    description: featuredCommunity.description,
                    header_image: featuredCommunity.header_image || undefined,
                    avatar_image: featuredCommunity.avatar_image || undefined,
                    members: featuredCommunity.members,
                    totalMembers: featuredCommunity.total_members,
                    workspace: featuredCommunity.workspace ? {
                      workspace_id: featuredCommunity.workspace.workspace_id,
                      workspace_name: featuredCommunity.workspace.workspace_name,
                      logo_url: featuredCommunity.workspace.logo_url || undefined
                    } : undefined
                  }}
                  onViewCommunity={() => handleCommunityView(featuredCommunity)}
                />
              </div>
              <div className="col-span-1 order-2">
                <CommunityAnnouncementCard />
              </div>
            </div>
          )}

          <Masonry
            breakpointCols={breakpointColumns}
            className="flex -mx-4 w-auto"
            columnClassName="px-4 space-y-6"
          >
            {/* Announcement Card first if no featured event */}
            {!featuredCommunity && <div><CommunityAnnouncementCard /></div>}

            {regularCommunities.map((community) => (
              <div key={community.id}>
                <CommunityCard
                  id={community.id}
                  name={community.name}
                  description={community.description}
                  imageUrl={community.header_image || undefined}
                  members={community.members}
                  highlighted={community.display_highlight}
                  workspace={community.workspace ? {
                    workspace_id: community.workspace.workspace_id,
                    workspace_name: community.workspace.workspace_name,
                    logo_url: community.workspace.logo_url || undefined
                  } : undefined}
                  onViewCommunity={() => handleCommunityView(community)}
                />
              </div>
            ))}
          </Masonry>
        </>
      ) : (
        !featuredCommunity && regularCommunities.length === 0 && <Empty />
      )}
    </div>
  );
};